import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { CountdownIcon } from '@/components/icons/CountdownIcon';
import { LiveIcon } from '@/components/icons/LiveIcon';
import { CompleteIcon } from '@/components/icons/CompleteIcon';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface CompetitionInfoProps {
  title: string;
  totalQuestions: number;
  correctPicks?: number;
  gameDate: Date;
  state: GameState;
  className?: string;
}

/**
 * CompetitionInfo - Displays game info at the top of Pick'em page
 * Shows: Icon + Title (left) | Picks count | Divider | Countdown (right)
 * This section scrolls away when user scrolls down
 */
const CompetitionInfo: React.FC<CompetitionInfoProps> = ({
  title,
  totalQuestions,
  correctPicks,
  gameDate,
  state,
  className = '',
}) => {
  const { t } = useTranslation();

  const getTimeDisplay = () => {
    if (state === 'live') return t('game.status.live');
    if (state === 'final') return t('game.status.complete');
    return formatTimeRemaining(gameDate);
  };

  const getTimeIcon = () => {
    switch (state) {
      case 'countdown':
      case 'upcoming':
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
      case 'live':
        return <LiveIcon className="w-4 h-4 flex-shrink-0" />;
      case 'final':
        return <CompleteIcon className="w-4 h-4 flex-shrink-0" />;
      default:
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
    }
  };

  return (
    <div
      className={`bg-[#2C2C2C] px-4 py-3 flex items-center justify-between ${className}`}
      data-test-id="competition-info"
    >
      {/* Left: Icon + Title */}
      <div className="flex items-center gap-2">
        {/* <img src="/nfl-icon.png" alt="League icon" className="w-8 h-8 object-contain" /> */}
        <span className="text-white font-gilroy font-bold text-base uppercase tracking-wide">
          {title}
        </span>
      </div>

      {/* Right: Picks count | Divider | Countdown */}
      <div className="flex items-center gap-3">
        <span className="text-white font-gilroy text-base">
          {correctPicks !== undefined
            ? `${correctPicks}/${totalQuestions} ${t('common.picks', { plural: totalQuestions !== 1 ? 's' : '' })}`
            : `${totalQuestions} ${t('common.picks', { plural: totalQuestions !== 1 ? 's' : '' })}`}
        </span>

        {/* Vertical divider */}
        <div className="h-5 w-px bg-gray-500"></div>

        <div className="flex items-center gap-1.5">
          {getTimeIcon()}
          <span className="text-white font-gilroy text-base">{getTimeDisplay()}</span>
        </div>
      </div>
    </div>
  );
};

export default CompetitionInfo;
